// Libs
import classNames from 'classnames/bind';
import { Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// Components, Layouts, Pages
import { ClientKanbanItem } from '~/components/module/client';
import {
  BaseFilters,
  BaseInputSearch,
  BaseKanban,
  BaseSelect,
  BaseToolbar,
  UnderDevelopment,
} from '~/components/common';
// Others
import {
  IBaseKanban,
  IBaseOption,
  IQueryParamBase,
  IResultCallApiFn,
  IStyleColumnKanban,
  ItemChangeKanban,
} from '~/utils/interface/common';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  optionsTypePage,
} from '~/utils/constants/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { useAppDispatch } from '~/utils/redux/hooks';
import { ItemTypeClient } from '~/utils/interface/client';
import { mockClientColumns } from '~/utils/mockData/client';
// Styles, images, icons
import styles from './ClientKanban.module.scss';
import { icons } from '~/assets';

type Props = {
  isTypePage?: string;
  onChangeType?: (value: string) => void;
};

const cx = classNames.bind(styles);

const ClientKanban = (props: Props) => {
  //#region Destructuring Props
  const { isTypePage, onChangeType } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [filterParams, setFilterParams] = useState<IQueryParamBase>(FILTER_DEFAULT_VALUES);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });

  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleItemChange = (item: ItemChangeKanban<IBaseKanban>) => {};

  const fetchOptionsTypeList = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsTypePage,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleAddClient = () => {
    handleShowUnderDevelopment();
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleApplyFilter = (values: IQueryParamBase | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
    });
  };

  const handleGetColorNameColumn = (id: string): IStyleColumnKanban => {
    switch (id) {
      case '1':
        return {
          backgroundColor: '#E6F4FF',
          color: '#0369A1',
          borderColor: '#BAE6FD',
        };
      case '2':
        return {
          backgroundColor: '#F0FDF4',
          borderColor: '#BBF7D0',
          color: '#15803D',
        };
      case '3':
        return {
          backgroundColor: '#FFF7ED',
          color: '#C2410C',
          borderColor: '#FED7AA',
        };
      default:
        return {
          backgroundColor: '#E6F4FF',
          color: '#0369A1',
          borderColor: '#BAE6FD',
        };
    }
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };
  //#endregion Handle Function
  return (
    <Box id='clientKanbanComponent' className={cx('container')}>
      <BaseToolbar title={t('client_title_clients_board')} total={t('client_title_total_clients', { total })}>
        <Box className={cx('button')}>
          <Button
            type='button'
            variant='outlined'
            className={cx('button')}
            onClick={handleAddClient}
            startIcon={<img src={icons.commonIconPlusPrimary} alt={'common_alt_img'} />}
          >
            {t('client_title_add_client')}
          </Button>
        </Box>

        <BaseInputSearch
          width={200}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        <BaseFilters
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        >
          {({ value, onChange }) => (
            <Stack direction={'column'} width={340} mb={4} gap={6}>
              {/* <BaseSelect
                gap={10}
                height={40}
                styleType='contained'
                label={t('client_title_filter_status')}
                placeholder={t('client_title_filter_all_status')}
                callApiFn={fetchOptionFilter}
                value={value?.status}
                onChange={(option) => handleFilterStatusChange(option as any, onChange)}
              /> */}
            </Stack>
          )}
        </BaseFilters>

        <BaseSelect
          gap={4}
          height={39}
          width={200}
          styleType='outlined'
          placeholder={t('common_text_placeholder_select', { ns: 'common/base' })}
          callApiFn={fetchOptionsTypeList}
          value={isTypePage}
          onChange={(item) => onChangeType?.(item?.value || EMPTY_STRING)}
        />
      </BaseToolbar>

      <BaseKanban
        data={mockClientColumns}
        styleColumn={handleGetColorNameColumn}
        onItemChange={handleItemChange}
        renderItem={(item: ItemTypeClient) => <ClientKanbanItem item={item} />}
      />

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default ClientKanban;
